import { isBrowser, isTestEnv } from "@buildwithflux/shared";
import { ClipperShape } from "./ClipperShape";
const isTestingOrCi = isTestEnv();
let loading = false;
let singleton = null;
const onLoadedCallbacks = new Set();
/**
 * This helper blocks the early-exit path an async call to initialize() to ensure that
 * it's execution can't prematurely attempt to access the clipper library. That can
 * occur when;
 * 1. other code has started initialization,
 * 2. this code attempts to initialize (but early exists because initialization is already
 * in progress due to the other code)
 * 3. this code proceeds to access the clipper library, which is not yet loaded, resulting
 * in a crash
 */
const anyCurrentInitializationRequest = async () => {
    let resolve;
    const onLoaded = () => {
        resolve?.();
    };
    const promise = new Promise((r) => {
        resolve = r;
    });
    onLoadedCallbacks.add(onLoaded);
    await promise;
    onLoadedCallbacks.delete(onLoaded);
};
export const Clipper = {
    /**
     * Available for manual initialization.
     * This is useful in non-react, async contexts, such as pcb layout engine
     */
    async initialize() {
        try {
            if (singleton) {
                return;
            }
            if (loading) {
                // If initialization already started be another call site, wait for that to
                // finish before exiting this initialization call. Doing so helps guard against
                // this call code assuming the library is ready (and attempting to prematurely
                // access - which will likely lead to a runtime exception)
                await anyCurrentInitializationRequest();
                return;
            }
            loading = true;
            const module = await import("js-angusj-clipper");
            if (!module) {
                throw new Error("Clipper module could not be imported");
            }
            const { loadNativeClipperLibInstanceAsync, NativeClipperLibRequestedFormat } = module;
            if (!loadNativeClipperLibInstanceAsync || !NativeClipperLibRequestedFormat) {
                throw new Error("Clipper module does not have the expected exports");
            }
            // Ordering is important here - eliminates possibility of initialization during async wasm
            // load request
            singleton = await loadNativeClipperLibInstanceAsync(isTestingOrCi || !isBrowser()
                ? NativeClipperLibRequestedFormat.AsmJsOnly
                : NativeClipperLibRequestedFormat.WasmOnly);
            loading = false;
            // Call any subscription callbacks, now that the library is loaded
            for (const onLoadedCallback of onLoadedCallbacks) {
                onLoadedCallback();
            }
        }
        catch (error) {
            loading = false;
            singleton = null;
            throw new Error(`Fatal error. Unable to load clipper library: ${error instanceof Error ? error.message : error}`);
        }
    },
    /**
     * Useful utilities for react, allows an external store sync hook to conditionally
     * render children in ClipperProvider
     */
    store: {
        subscribe(callback) {
            onLoadedCallbacks.add(callback);
            return () => {
                onLoadedCallbacks.delete(callback);
            };
        },
        snapshot() {
            return singleton;
        },
    },
    // TODO:
    // containsPoint()
    /**
     * Provides synchronous access to shape creation via the Clipper Shape Factory
     */
    shape(polygons, closed = true) {
        if (!singleton) {
            throw new Error(`Accessing Clipper before it's loaded.

Ensure access is within <ClipperProvider> for React contexts, or that initialize() has been called in a non-react, async use cases.`);
        }
        return new ClipperShape(singleton, polygons, closed);
    },
    /* TODO: remove */
    shapeFromClipperPaths(paths, closed = true) {
        // TODO: remove this and just use "clone()" method
        if (!singleton) {
            throw new Error(`Accessing Clipper before it's loaded.

Ensure access is within <ClipperProvider> for React contexts, or that initialize() has been called in a non-react, async use cases.`);
        }
        return ClipperShape.fromClipperPaths(singleton, paths, closed);
    },
    fromShape(shape, options) {
        return Clipper.shape([shape.getPoints(options?.divisions)], options?.closed ?? true);
    },
    fromObject(object) {
        if (!singleton) {
            throw new Error(`Accessing Clipper before it's loaded.
Ensure access is within <ClipperProvider> for React contexts, or that initialize() has been called in a non-react, async use cases.`);
        }
        return ClipperShape.fromObject(singleton, object);
    },
};
// Automatic initialization is an intentional side effect of importing this module
if (!isTestingOrCi) {
    Clipper.initialize();
}
//# sourceMappingURL=clipper.js.map